import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  getDefaultNodeData,
  getDefaultNeurodeData,
} from "./utils/getDefaultNodeData";
import type { Variables, VariableCategory } from "./types";
import { type ConfigId } from "@/constants/ConfigData";
import { axiosGet, axiosPost } from "@/services/axiosService";

import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  reconnectEdge,
  getIncomers,
  type Node,
  type Edge,
  type Connection,
  type XYPosition,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
} from "@xyflow/react";

interface State {
  nodes: Array<Node<Record<string, any>>>;
  edges: Array<Edge>;
  metaData: Record<string, any>;
  selectedNodeId: string;
  nextNodeId: number;
  variables: Variables;
  openConfigModalId: ConfigId | "";
  segmentVars: Array<any>;
  isSelPanelSCollapse: boolean;
  isWhatsappLoading: boolean;
  isFacebookLoading: boolean;
  isInstagramLoading: boolean;
  nodeErrors: Record<string, Array<string>>;
  leadsData: Array<Record<string, any>>;
  leadsSaveTimer: NodeJS.Timeout | null;
  edgeHoverId: string | null;
  sourceScroll: boolean;
  integrationHealth: IntegrationHealth[];
  integrationHealthLoaded: boolean;
}

interface IntegrationHealth {
  type: string;
  status: "healthy" | "degraded" | "broken" | "unchecked";
  lastChecked: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  latencyMs: number | null;
}

interface Actions {
  handleNodesChange: OnNodesChange;
  handleEdgesChange: OnEdgesChange;
  handleReconnect: (oldEdge: Edge, newConnection: Connection) => void;
  addNode: (type: string, position: XYPosition) => void;
  removeNode: (id: string) => void;
  duplicateNode: (copyId: string) => void;
  selectNode: (id: string) => void;
  addEdge: OnConnect;
  removeEdge: (id: string) => void;
  setMetaData: (data: Record<string, any>) => void;
  setVariables: (node: Node) => void;
  setOpenConfigModalId: (configId: ConfigId | "") => void;
  fetchSegmentData: (listId: string) => void;
  updateLeadsData: (leadId: string, data: Record<string, any>) => void;
  setIsSelPanelSCollapse: (value: boolean) => void;
  setIsWhatsappLoading: (value: boolean) => void;
  setIsFacebookLoading: (value: boolean) => void;
  setIsInstagramLoading: (value: boolean) => void;
  setNodeErrors: (nodeErrors: Record<string, Array<string>>) => void;
  setCampaignData: (data: Record<string, any>) => void;
  setEdgeHoverId: (id: string | null) => void;
  setNodes: (nodes: Array<Node<Record<string, any>>>) => void;
  setSourceScroll: (scroll: boolean) => void;
  fetchIntegrationHealth: () => void;
}

const useStore = create<State & Actions>()(
  immer((set, get) => ({
    nodes: [],
    edges: [],
    metaData: {},
    selectedNodeId: "",
    nextNodeId: 1,
    variables: {},
    openConfigModalId: "",
    segmentVars: [],
    isSelPanelSCollapse: true,
    isWhatsappLoading: false,
    isFacebookLoading: false,
    isInstagramLoading: false,
    nodeErrors: {},
    leadsData: [],
    leadsSaveTimer: null,
    edgeHoverId: null,
    sourceScroll: false,
    integrationHealth: [],
    integrationHealthLoaded: false,
    handleNodesChange: (changes) =>
      set((state) => {
        state.nodes = applyNodeChanges(changes, state.nodes);
      }),
    handleEdgesChange: (changes) => {
      set((state) => {
        state.edges = applyEdgeChanges(changes, state.edges);
      });
    },
    handleReconnect: (oldEdge, newConnection) => {
      set((state) => {
        state.edges = reconnectEdge(oldEdge, newConnection, state.edges);
      });
    },
    addNode: (type, position) => {
      set((state) => {
        const id = String(state.nextNodeId);
        state.nodes.push({
          id,
          type,
          position,
          data:
            getDefaultNodeData(id, type) || getDefaultNeurodeData(type) || {},
        });
        state.nextNodeId += 1;
      });
    },
    removeNode: (id) => {
      set((state) => {
        state.nodes = state.nodes.filter((node) => node.id !== id);
        state.edges = state.edges.filter(
          (edge) => edge.source !== id && edge.target !== id,
        );

        state.selectedNodeId = "";
      });
    },
    duplicateNode: (copyId) => {
      set((state) => {
        const node = state.nodes.find((node) => node.id === copyId);
        if (!node) return;

        const id = String(state.nextNodeId);
        const { type, position, data } = node;

        state.nodes.push({
          id,
          type,
          position: {
            x: position.x,
            y: position.y + 150,
          },
          data,
        });
        state.nextNodeId += 1;
      });
    },
    selectNode: (id) => {
      set((state) => {
        state.selectedNodeId = id;
      });
    },
    addEdge: (connection) => {
      set((state) => {
        const edge = {
          ...connection,
          type: "custom",
          updatable: "target",
        };

        state.edges = addEdge(edge, state.edges);
      });
    },
    removeEdge: (id) => {
      set((state) => {
        state.edges = state.edges.filter((edge) => edge.id !== id);
      });
    },
    setMetaData: (data) => {
      set((state) => {
        Object.assign(state.metaData, data);
      });
    },
    setVariables: async (node) => {
      try {
        const variables = await findVariables(node, get().nodes, get().edges);
        set((state) => {
          state.variables = variables;
        });
      } catch (error) {
        console.error("Error setting variables:", error);
      }
    },
    setOpenConfigModalId: (configId) => {
      set((state) => {
        state.openConfigModalId = configId;
      });
    },
    fetchSegmentData: async (listId) => {
      const tagPromise = axiosGet(`/audience/tags?list_id=${listId}`);
      const leadPromise = axiosGet(
        `/audience/leads?list_id=${listId}&type=upload`,
      );

      const [tagResponse, leadResponse] = await Promise.all([
        tagPromise,
        leadPromise,
      ]);

      set((state) => {
        state.segmentVars = tagResponse.data.data;
        state.leadsData = leadResponse.data.data;
      });
    },
    updateLeadsData: async (leadId, data) => {
      set((state) => {
        for (const lead of state.leadsData) {
          if (lead._id === leadId) {
            Object.assign(lead, data);
            break;
          }
        }

        if (state.leadsSaveTimer) clearTimeout(state.leadsSaveTimer);
        state.leadsSaveTimer = setTimeout(() => {
          axiosPost("/audience/editLeads", get().leadsData);
        }, 3000);
      });
    },
    setIsSelPanelSCollapse: (value) => {
      set((state) => {
        state.isSelPanelSCollapse = value;
      });
    },
    setIsWhatsappLoading: (value) => {
      set((state) => {
        state.isWhatsappLoading = value;
      });
    },
    setIsFacebookLoading: (value) => {
      set((state) => {
        state.isFacebookLoading = value;
      });
    },
    setIsInstagramLoading: (value) => {
      set((state) => {
        state.isInstagramLoading = value;
      });
    },
    setNodeErrors: (nodeErrors) => {
      set((state) => {
        state.nodeErrors = nodeErrors;
      });
    },
    setCampaignData: (data) => {
      set((state) => {
        Object.assign(state.nodes[0]?.data?.audienceData, data);
      });
    },
    setEdgeHoverId: (id) => {
      set((state) => {
        state.edgeHoverId = id;
      });
    },
    setNodes: (nodes) => {
      set((state) => {
        state.nodes = nodes;
      });
    },
    setSourceScroll: (scroll) => {
      set((state) => {
        state.sourceScroll = scroll;
      });
    },
    fetchIntegrationHealth: async () => {
      try {
        const response = await axiosGet("/integration/health-status");
        if (response.data?.success) {
          set((state) => {
            state.integrationHealth = response.data.data;
            state.integrationHealthLoaded = true;
          });
        }
      } catch {
        // Non-critical — health data is optional
        set((state) => {
          state.integrationHealthLoaded = true;
        });
      }
    },
  })),
);

const nonEditableFields = [
  "ConvertedDate",
  "HasOptedOutOfEmail",
  "Latitude",
  "NumberOfEmployees",
  "Industry",
  "Status",
  "Photo URL",
  "MasterRecordId",
  "LastModifiedDate",
  "Address",
  "LastReferencedDate",
  "Name",
  "ActivityMetricId",
  "CreatedById",
  "IsDeleted",
  "IsConverted",
  "LastViewedDate",
  "SystemModstamp",
  "ActivityMetricRollupId",
  "CreatedDate",
  "FirstCallDateTime",
  "ActionCadenceAssigneeId",
  "IndividualId",
  "EmailBouncedDate",
  "EmailBouncedReason",
  "JigsawContactId",
  "LastActivityDate",
  "IsUnreadByOwner",
  "ConvertedOpportunityId",
  "ConvertedContactId",
  "ConvertedAccountId",
  "LastModifiedById",
  "Id",
  "attributes",
  "ActionCadenceId",
  "ActiveTrackerCount",
  "LeadSource",
  "OwnerId",
  "LastTransferDate",
  "LastModifiedById",
  "FirstEmailDateTime",
  "ScheduledResumeDateTime",
  "ActionCadenceState",
  "Rating",
  "GeocodeAccuracy",
];

const initialState = useStore.getState();
export const useResetStore = () => useStore.setState(initialState, true);

export default useStore;

async function findVariables(
  node: Node,
  nodes: Array<Node>,
  edges: Array<Edge>,
): Promise<Variables> {
  const queue: Array<Node> = [node];
  const visited: Set<string> = new Set();
  const variables: Variables = {};

  while (queue.length) {
    const curNode = queue.shift();
    if (!curNode) break;

    for (const incomer of getIncomers(curNode, nodes, edges)) {
      if (!incomer.type || visited.has(incomer.id)) continue;

      const variableData = await getVariableData(
        incomer.id,
        incomer.type,
        incomer.data,
      );

      for (const name of variableData.names) {
        const category = variableData.category ?? "node";

        if (!variables[category]) variables[category] = [];
        variables[category]?.push({
          id: `${incomer.id}_${name}`,
          name,
          type: "string",
          originNode: {
            id: incomer.id,
            type: incomer.type,
          },
        });
      }

      queue.push(incomer);
      visited.add(incomer.id);
    }
  }

  return variables;
}

export function getVariableData(
  id: string,
  type: string,
  data: any,
): {
  names: Array<string>;
  category?: VariableCategory;
} {
  switch (type) {
    case "campaign": {
      const campaignType = useStore.getState().metaData.campaignType;

      if (campaignType === "socialData") {
        return {
          names: data.webhookData.variables.map((item) => item.customName),
          category: "webhook",
        };
      }

      if (campaignType === "segment") {
        const segmentVars = useStore.getState().segmentVars;

        return {
          names: segmentVars.map((item) => item.name),
          category: "audience",
        };
      }

      return {
        names: [],
      };
    }

    case "salesForce": {
      const campaignType = useStore.getState().metaData.campaignType;

      if (campaignType === "segment" || campaignType === "socialData") {
        const nodes = useStore
          .getState()
          .nodes.filter((node: any) => node.type === "salesForce");

        if (nodes.length === 0) {
          console.error("No Salesforce nodes found.");
          return {
            names: [],
            category: "salesForce" as const,
          };
        }

        const filteredAndFormattedData: Record<string, any> = {};

        nodes.forEach((node: any) => {
          const salesForceData = node.data?.salesForceData;

          if (salesForceData && salesForceData.payload) {
            const { payload } = salesForceData;

            for (const [key, value] of Object.entries(payload)) {
              if (!nonEditableFields.includes(key)) {
                const formattedKey = `${key}`;
                filteredAndFormattedData[formattedKey] = value;
              }
            }
          }
        });

        return {
          names: Object.keys(filteredAndFormattedData),
          category: "salesForce" as const,
        };
      }

      return {
        names: [],
        category: "salesForce" as const,
      };
    }

    case "openai":
    case "ask_question":
    case "email":
    case "phone":
    case "name":
    case "address":
    case "company":
    case "job":
    case "username":
    case "result":
    case "assignees": {
      return {
        names: [data.variableName],
      };
    }
    case "button": {
      return {
        names: [data.variableName, data.buttonDetails.variableName],
      };
    }
    case "sendEmail": {
      return {
        names: [
          `${id}_email_status`,
          `${id}_email_opened`,
          // `${id}_email_replied`,
        ],
        category: "email",
      };
    }
    case "hitApi": {
      return {
        names: data.hitApiDetails.variables.map(
          (variable) => variable.customName,
        ),
        category: "api",
      };
    }
    case "linkedIn": {
      const visitVariables = [
        `${id}_firstName`,
        `${id}_lastName`,
        `${id}_location`,
        `${id}_headline`,
        `${id}_industry`,
        `${id}_summary`,
      ];

      return {
        names: [`${id}_profileId`].concat(
          data.linkedInData?.nodeType === "visit" ? visitVariables : [],
        ),
        category: "linkedIn",
      };
    }
    case "formNode": {
      const fieldNames = (data.formDetails?.fields || [])
        .map(
          (field: any) =>
            field.name ||
            (field.label || "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "_")
              .replace(/^_|_$/g, ""),
        )
        .filter(Boolean);
      return {
        names: [...fieldNames, `${id}_form_submitted`, `${id}_form_timestamp`],
        category: "form" as any,
      };
    }
    case "csvParser": {
      return {
        names: [`${id}_rows`, `${id}_columns`, `${id}_rowCount`],
        category: "custom" as any,
      };
    }
    case "htmlExtractor": {
      return {
        names: [`${id}_extracted`, `${id}_matchCount`],
        category: "custom" as any,
      };
    }
    case "xmlParser": {
      return {
        names: [`${id}_parsed`],
        category: "custom" as any,
      };
    }
    case "jsonTransform": {
      return {
        names: [`${id}_transformed`],
        category: "custom" as any,
      };
    }
    case "webhookTrigger": {
      return {
        names: [`${id}_payload`, `${id}_event`, `${id}_timestamp`],
        category: "webhook" as any,
      };
    }
    case "waitForWebhook": {
      return {
        names: [`${id}_callback_data`, `${id}_callback_status`],
        category: "custom" as any,
      };
    }
    case "loopIterator": {
      return {
        names: [
          `__loop_item`,
          `__loop_index`,
          `__loop_total`,
          `${id}_loop_results`,
        ],
        category: "custom" as any,
      };
    }
    case "splitInBatches": {
      return {
        names: [
          `__batch_items`,
          `__batch_index`,
          `__batch_total`,
          `${id}_batch_results`,
        ],
        category: "custom" as any,
      };
    }
    case "merge": {
      return {
        names: [data?.mergeConfig?.outputVariable || `${id}_merged_data`],
        category: "custom" as any,
      };
    }
    case "errorHandler": {
      const ehConfig = data?.errorHandlerConfig || {};
      return {
        names: [
          ehConfig.errorVariable || "error_message",
          ehConfig.errorTypeVariable || "error_type",
          "error_step_id",
        ],
        category: "custom" as any,
      };
    }
    case "runCode": {
      return {
        names: [data?.outputVariable || "code_output"],
        category: "custom" as any,
      };
    }
    default: {
      return {
        names: [],
      };
    }
  }
}
